import { CircleHelpIcon, LogOutIcon, SettingsIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { rechtenTekst } from '@vip/shared';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { useAuth } from '../../features/auth/useAuth';

/** Zelfde afleiding als de zijbalk: het pad bepaalt de titel, zodat er geen
 *  tweede lijst met paden ontstaat die uit de pas kan lopen. */
const TITELS: Array<[pad: string, titel: string]> = [
  ['/gesprekken', 'Mijn gesprekken'],
  ['/overzicht', 'Dashboard'],
  ['/hulpvragen', 'Hulpvragen'],
];

const initialen = (naam: string) =>
  naam
    .split(/\s+/)
    .slice(0, 2)
    .map((deel) => deel[0]?.toUpperCase() ?? '')
    .join('');

export function SiteHeader({ onUitleg }: { onUitleg: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const titel = TITELS.find(([pad]) => location.pathname.startsWith(pad))?.[1];

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
      <SidebarTrigger className="-ml-1" />
      <Separator className="mr-2 h-4" orientation="vertical" />
      {titel && <h1 className="text-base font-medium">{titel}</h1>}

      <div className="ml-auto">
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="gap-2" size="sm" variant="ghost">
                <Avatar className="size-6">
                  <AvatarFallback className="text-xs">{initialen(user.naam)}</AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline">{user.naam}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>{user.naam}</span>
                  <span className="text-xs font-normal text-muted-foreground">
                    {rechtenTekst(user.rechten)}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={onUitleg}>
                  <CircleHelpIcon />
                  Uitleg
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/instellingen')}>
                  <SettingsIcon />
                  Instellingen
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate('/login');
                  }}
                >
                  <LogOutIcon />
                  Uitloggen
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
